/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Universal Shared Types & Schemas for Admin Audit Logging (Task 11.1.2)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import { z } from 'zod';

/**
 * Standard Admin Audit Actions catalog.
 */
export const AdminAuditAction = {
  // Financial & Commerce Actions
  ACTION_APPROVE_ORDER: 'ACTION_APPROVE_ORDER',
  ACTION_REJECT_ORDER: 'ACTION_REJECT_ORDER',
  ACTION_REFUND_ORDER: 'ACTION_REFUND_ORDER',
  ACTION_ADJUST_CREDITS: 'ACTION_ADJUST_CREDITS',

  // AI & Model Management Actions
  ACTION_UPDATE_MODEL: 'ACTION_UPDATE_MODEL',
  ACTION_TOGGLE_MODEL: 'ACTION_TOGGLE_MODEL',
  ACTION_UPDATE_PROVIDER: 'ACTION_UPDATE_PROVIDER',
  ACTION_RESET_CIRCUIT_BREAKER: 'ACTION_RESET_CIRCUIT_BREAKER',

  // User & Workspace Moderation
  ACTION_IMPERSONATE: 'ACTION_IMPERSONATE',
  ACTION_STOP_IMPERSONATION: 'ACTION_STOP_IMPERSONATION',
  ACTION_CREATE_USER: 'ACTION_CREATE_USER',
  ACTION_UPDATE_USER: 'ACTION_UPDATE_USER',
  ACTION_SUSPEND_USER: 'ACTION_SUSPEND_USER',
  ACTION_ACTIVATE_USER: 'ACTION_ACTIVATE_USER',
  ACTION_UPDATE_WORKSPACE: 'ACTION_UPDATE_WORKSPACE',

  // System & Seed Asset Actions
  ACTION_SYNC_SEED_ASSETS: 'ACTION_SYNC_SEED_ASSETS',
  ACTION_UPDATE_SETTING: 'ACTION_UPDATE_SETTING',
  ACTION_FLUSH_CACHE: 'ACTION_FLUSH_CACHE',

  // Generic Fallback
  ACTION_GENERIC_MUTATION: 'ACTION_GENERIC_MUTATION',
} as const;

export type AdminAuditActionType = (typeof AdminAuditAction)[keyof typeof AdminAuditAction] | string;

/**
 * Zod schema for a single Admin Audit Log item.
 */
export const AdminAuditLogItemSchema = z.object({
  id: z.string().describe('Unique identifier for the audit log record'),
  adminUserId: z.string().describe('ID of the super admin who performed the action'),
  adminUserEmail: z.string().optional().describe('Email of the admin user if joined'),
  adminUserName: z.string().optional().describe('Full name of the admin user if joined'),
  action: z.string().describe('Standardized action identifier (e.g., ACTION_APPROVE_ORDER)'),
  targetType: z.string().nullable().optional().describe('Category of resource affected (e.g., order, user)'),
  targetId: z.string().nullable().optional().describe('Identifier of the affected resource'),
  clientIp: z.string().describe('IPv4/IPv6 address of the admin client'),
  userAgent: z.string().nullable().optional().describe('User-Agent header of the admin browser/client'),
  beforeState: z.record(z.unknown()).nullable().optional().describe('State snapshot before mutation'),
  afterState: z.record(z.unknown()).nullable().optional().describe('State snapshot after mutation'),
  metadata: z.record(z.unknown()).nullable().optional().describe('Supplemental contextual information'),
  createdAt: z.number().describe('Unix timestamp in seconds when the action occurred'),
});

export type AdminAuditLogItem = z.infer<typeof AdminAuditLogItemSchema>;

/**
 * Query schema for filtering & paginating admin audit logs.
 */
export const AdminAuditLogListQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1).describe('Page number (1-based)'),
  limit: z.coerce.number().int().min(1).max(100).default(20).describe('Items per page'),
  adminUserId: z.string().optional().describe('Filter by specific admin user ID'),
  action: z.string().optional().describe('Filter by specific action identifier'),
  targetType: z.string().optional().describe('Filter by resource target type'),
  targetId: z.string().optional().describe('Filter by resource target ID'),
  startDate: z.string().optional().describe('ISO 8601 or YYYY-MM-DD start filter'),
  endDate: z.string().optional().describe('ISO 8601 or YYYY-MM-DD end filter'),
  search: z.string().optional().describe('Fuzzy search keyword on action, target, or metadata'),
});

export type AdminAuditLogListQuery = z.infer<typeof AdminAuditLogListQuerySchema>;

/**
 * Response schema for paginated audit log list.
 */
export const AdminAuditLogListResponseSchema = z.object({
  items: z.array(AdminAuditLogItemSchema).describe('List of audit log records'),
  total: z.number().describe('Total records matching the filter criteria'),
  page: z.number().describe('Current page number'),
  limit: z.number().describe('Page limit'),
  totalPages: z.number().describe('Total number of pages'),
});

export type AdminAuditLogListResponse = z.infer<typeof AdminAuditLogListResponseSchema>;

/**
 * Parameters for recording an audit entry.
 */
export interface RecordAdminAuditParams {
  adminUserId: string;
  action: AdminAuditActionType;
  targetType?: string | null;
  targetId?: string | null;
  clientIp: string;
  userAgent?: string | null;
  beforeState?: Record<string, unknown> | null;
  afterState?: Record<string, unknown> | null;
  metadata?: Record<string, unknown> | null;
}
